import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message } from "antd";
import { get, post, put } from "../../../utils/apiUtils";
import { getUserRole } from "../../../hooks/getUserRoles";
import GeneralButton from "../../../components/GeneralButton";
import { useGate } from "../../Subscription/entitlements";
import { normalizeRole, isWorkingSeat } from "../../../utils/roles";
import { useTranslation } from "react-i18next";

const { Option } = Select;

function MemberModal({ visible, onCancel, onCreate, memberId, mode }: any) {
  const { t } = useTranslation("userManagement");
  const UserRole = getUserRole();
  const gate = useGate();
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [roles, setRoles] = useState<any>([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [cities, setCities] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  // Edit mode only: was this member ALREADY a working seat (Owner/Manager/
  // Analyst) before this edit? Moving them between working roles, or down to
  // a free one, never consumes an additional seat — only promoting someone
  // who was previously a FREE role (Contributor/Viewer) does.
  const [originalRoleIsWorkingSeat, setOriginalRoleIsWorkingSeat] =
    useState(false);

  // Can we still assign a PAID working seat (Manager/Analyst)? Only on a paid
  // plan that has room. On Free the sole working seat is the Owner, so only the
  // free roles (Contributor/Viewer) can be added.
  const canAddWorkingSeat =
    gate.isPaid &&
    (!gate.seatUsage ||
      gate.seatUsage.workingUserLimit == null ||
      gate.seatUsage.workingUsersUsed < gate.seatUsage.workingUserLimit);

  // Does selecting a working role in THIS modal need the seat check? Always
  // in create mode; in edit mode, only when the member isn't already a
  // working seat (see originalRoleIsWorkingSeat above).
  const needsSeatCheck =
    mode === "create" || (mode === "edit" && !originalRoleIsWorkingSeat);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (["edit", "view"].includes(mode) && memberId) {
        try {
          const response = await get<any>(`members/${memberId}/edit-details`);
          if (response?.type === "success") {
            const {
              username,
              email,
              country_id,
              city_id,
              workspace_ids,
              role_id,
            } = response.data;

            await handleCountryChange(country_id.toString());

            // Prit derisa te ngarkohen rolet, pastaj bej setFieldsValue
            const waitForRoles = () =>
              new Promise((resolve) => {
                const check = () => {
                  if (roles.length > 0) resolve(true);
                  else setTimeout(check, 100); // provo prap pas 100ms
                };
                check();
              });

            await waitForRoles();

            const matchedRole = roles.find((r: any) => r.id === role_id);
            setOriginalRoleIsWorkingSeat(
              isWorkingSeat(normalizeRole(matchedRole?.name)),
            );

            form.setFieldsValue({
              fullName: username,
              email,
              country: country_id,
              city: city_id,
              workspaces: workspace_ids,
              role: role_id,
            });
          } else {
            message.error(
              t("memberModal.messages.fetchMemberDetailsError", {
                error: response.type,
              })
            );
          }
        } catch (error: any) {
          message.error(
            t("memberModal.messages.fetchMemberDetailsError", {
              error: error.message,
            })
          );
        }
      }
    };

    if (visible) fetchMemberDetails();
  }, [memberId, mode, form, visible, roles]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get<any>("countries/");
        if (response.type === "success") {
          setCountries(response.data);
        } else {
          message.error(
            response.data?.response?.data?.detail ||
              response.data?.response?.data?.message ||
              response.data?.message ||
              t("memberModal.messages.loadCountriesError")
          );
        }
      } catch (error) {
        message.error(t("memberModal.messages.loadCountriesError"));
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response =
          normalizeRole(UserRole) === "manager"
            ? await get<any>("Users/project-roles/")
            : await get<any>("Users/roles/");

        if (response.type === "success") {
          let availableRoles = response.data;

          if (normalizeRole(UserRole) === "owner" && mode === "create") {
            availableRoles = availableRoles.filter(
              (role: any) => normalizeRole(role.name) !== "owner"
            );
          }

          setRoles(availableRoles);
        } else {
          message.error(
            response.data?.response?.data?.detail ||
              response.data?.response?.data?.message ||
              response.data?.message ||
              t("memberModal.messages.loadRolesError")
          );
        }
      } catch (error) {
        message.error(t("memberModal.messages.loadRolesError"));
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const response = await get<any>("get-all-workspaces");
        if (response.type === "success") {
          setWorkspaces(response.data);
        }
      } catch (error) {
        message.error(t("memberModal.messages.loadWorkspaceError"));
      }
    };
    fetchWorkspaces();
  }, []);

  const handleCountryChange = async (countryId: string) => {
    form.setFieldsValue({ city: undefined });
    try {
      const response = await get<any>(`countries/${countryId}/cities/`);
      if (response.type === "success") {
        setCities(response.data);
      } else {
        setCities([]);
        message.error(t("memberModal.messages.loadCitiesError"));
      }
    } catch (error) {
      message.error(t("memberModal.messages.loadCitiesError"));
      setCities([]);
    }
  };

  const doSubmit = async (values: any) => {
    setLoading(true);

    const payload = {
      username: values.fullName,
      email: values.email,
      country_id: values.country,
      city_id: values.city,
      workspace_ids: values.workspaces,
      role_id: values.role,
    };

    try {
      const response =
        mode === "edit"
          ? await put<any>(`members/${memberId}/edit`, payload)
          : await post<any>("Users/add-member/", payload);

      console.log("Response:", response);
      if (response?.type === "success") {
        message.success(
          mode === "edit"
            ? t("memberModal.messages.memberUpdatedSuccess")
            : t("memberModal.messages.memberCreatedSuccess")
        );
        onCreate(values);
        form.resetFields();
      } else {
        message.error(
          t("memberModal.messages.submitError", {
            detail: response.data.response?.data.detail,
          })
        );
      }
    } catch (error: any) {
      message.error(
        t("memberModal.messages.submitFailed", { error: error.message })
      );
    } finally {
      setLoading(false);
      onCancel(() => form.resetFields());
    }
  };

  const handleSubmit = async (values: any) => {
    // Seat gate: assigning a working role (Owner/Manager/Analyst) beyond the
    // plan's working-user cap opens the seat-limit prompt. Contributor/Viewer
    // are free & uncapped. In edit mode, a member who already occupies a
    // working seat never consumes an additional one — skip the gate only
    // then; promoting someone from a free role still needs it, same as create.
    if (mode === "edit" && originalRoleIsWorkingSeat) {
      doSubmit(values);
      return;
    }
    const roleName = roles.find((r: any) => r.id === values.role)?.name;
    gate.requireSeat(normalizeRole(roleName), () => doSubmit(values));
  };

  useEffect(() => {
    if (visible && mode === "create") {
      form.resetFields();
      setOriginalRoleIsWorkingSeat(false);
    }
  }, [visible, mode, form]);

  return (
    <Modal
      open={visible}
      title={
        mode === "edit"
          ? t("memberModal.titles.edit")
          : mode === "view"
          ? t("memberModal.titles.view")
          : t("memberModal.titles.create")
      }
      className="user-management-modal"
      onCancel={onCancel}
      footer={
        mode === "view" ? (
          <GeneralButton
            variant="default"
            onClick={onCancel}
            className="cancel-member-button"
          >
            {t("memberModal.buttons.close")}
          </GeneralButton>
        ) : (
          <div
            style={{ display: "flex", justifyContent: "center", gap: "10px" }}
          >
            <GeneralButton onClick={onCancel} className="cancel-member-button">
              {t("memberModal.buttons.cancel")}
            </GeneralButton>
            <GeneralButton
              loading={loading}
              onClick={() => {
                form
                  .validateFields()
                  .then((values) => {
                    handleSubmit(values);
                  })
                  .catch((info) => {
                    console.log("Validation failed:", info);
                  });
              }}
              className="add-edit-member-button"
            >
              {mode === "edit"
                ? t("memberModal.buttons.saveChanges")
                : t("memberModal.buttons.addNewMember")}
            </GeneralButton>
          </div>
        )
      }
    >
      <Form form={form} layout="vertical" className="form-style">
        <label className="personal-information">
          {t("memberModal.sections.personalInformation")}
        </label>
        <div className="first-section-modal">
          <Form.Item
            name="fullName"
            label={t("memberModal.fields.fullNameLabel")}
            rules={[
              {
                required: true,
                message: t("memberModal.fields.fullNameRequired"),
              },
            ]}
            className="title-style"
          >
            <Input disabled={mode === "view"} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t("memberModal.fields.emailLabel")}
            rules={[
              {
                required: true,
                message: t("memberModal.fields.emailRequired"),
                type: "email",
              },
            ]}
            className="title-style"
          >
            <Input disabled={mode === "view"} />
          </Form.Item>
        </div>
        <div className="first-section-modal">
          <Form.Item
            name="workspaces"
            label={t("memberModal.fields.workspacesLabel")}
            rules={[
              {
                required: true,
                message: t("memberModal.fields.workspacesRequired"),
              },
            ]}
            className="title-style"
          >
            <Select
              disabled={mode === "view"}
              mode="multiple"
              placeholder={t("memberModal.fields.workspacesPlaceholder")}
              filterOption={(input: any, option: any) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {workspaces.map((w: any) => (
                <Option key={w.id} value={w.id}>
                  {w.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="role"
            label={t("memberModal.fields.roleLabel")}
            rules={[
              { required: true, message: t("memberModal.fields.roleRequired") },
            ]}
            className="title-style"
            extra={
              needsSeatCheck && !canAddWorkingSeat
                ? t("memberModal.seatLimitNotice")
                : undefined
            }
          >
            <Select
              disabled={mode === "view"}
              placeholder={t("memberModal.fields.rolePlaceholder")}
              showSearch
              filterOption={(input: any, option: any) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {roles
                .filter((role: any) =>
                  !needsSeatCheck || canAddWorkingSeat
                    ? true
                    : !isWorkingSeat(normalizeRole(role.name)),
                )
                .map((role: any) => (
                  <Option key={role.id} value={role.id}>
                    {role.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>
        <label className="personal-information">
          {t("memberModal.sections.location")}
        </label>
        <div className="third-section-modal">
          <Form.Item
            name="country"
            label={t("memberModal.fields.countryLabel")}
            rules={[
              {
                required: true,
                message: t("memberModal.fields.countryRequired"),
              },
            ]}
            className="title-style"
          >
            <Select
              showSearch
              placeholder={t("memberModal.fields.countryPlaceholder")}
              disabled={mode === "view"}
              onChange={handleCountryChange}
              filterOption={(input: any, option: any) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {countries.map((country: any) => (
                <Option key={country.id} value={country.id}>
                  {country.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="city"
            label={t("memberModal.fields.cityLabel")}
            className="title-style"
          >
            <Select
              showSearch
              placeholder={t("memberModal.fields.cityPlaceholder")}
              disabled={!form.getFieldValue("country") || mode === "view"}
              filterOption={(input: any, option: any) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {cities.map((city: any) => (
                <Option key={city.id} value={city.id}>
                  {city.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}

export default MemberModal;
